import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, Copy, Users, Gift, TrendingUp } from "lucide-react";

export default function Referrals() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [referred, setReferred] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!currentUser || !userProfile?.referralCode) { setLoading(false); return; }
    async function fetchReferrals() {
      try {
        const usersSnap = await get(ref(db, "users"));
        if (usersSnap.exists()) {
          const all = Object.entries(usersSnap.val()).map(([uid, u]) => ({ uid, ...u }));
          const mine = all.filter(u => u.referredBy === userProfile.referralCode);
          setReferred(mine.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
        }
      } catch (e) {  }
      setLoading(false);
    }
    fetchReferrals();
  }, [currentUser, userProfile?.referralCode]);

  function copyCode() {
    const text = `Join CampusSwap! Use my code ${userProfile?.referralCode} to get verified faster. ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (!currentUser) return null;

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white border-b px-4 py-4 sticky top-14 z-10 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <h2 className="text-lg font-bold text-gray-800">Referrals</h2>
      </div>

      <div className="px-4 pt-4 space-y-4 max-w-lg mx-auto">
        {/* Stats */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-5 text-white">
          <div className="flex items-center gap-3 mb-4">
            <Gift size={28} className="text-yellow-300" />
            <div>
              <p className="font-bold text-lg">Your Referral Code</p>
              <p className="text-blue-200 text-xs">Share with friends — both get benefits!</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-xl px-4 py-3 flex-1 text-center font-bold text-2xl tracking-widest">
              {userProfile?.referralCode || "—"}
            </div>
            <button onClick={copyCode}
              className="bg-white text-blue-600 px-4 py-3 rounded-xl font-semibold text-sm flex items-center gap-1.5 flex-shrink-0">
              {copied ? "✓ Copied!" : <><Copy size={15} /> Copy</>}
            </button>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl shadow-sm p-4">
          <div className="flex items-center gap-3 mb-1">
            <TrendingUp size={18} className="text-indigo-500" />
            <p className="font-semibold text-gray-800">Your Impact</p>
          </div>
          <p className="text-3xl font-bold text-blue-600 mt-2">{referred.length}</p>
          <p className="text-sm text-gray-500">friend{referred.length !== 1 ? "s" : ""} joined using your code</p>
          {referred.length >= 5 && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              <p className="text-xs font-semibold text-green-700">🎉 You're a top referrer! Keep inviting friends for more trust points.</p>
            </div>
          )}
        </div>

        {/* Referred users list */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2">
            <Users size={16} className="text-blue-500" />
            <p className="font-semibold text-gray-800 text-sm">People You Referred</p>
          </div>
          {loading ? (
            [1,2,3].map(i => <div key={i} className="h-14 mx-4 my-2 bg-gray-100 rounded-xl animate-pulse" />)
          ) : referred.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Users size={36} className="mx-auto text-gray-200 mb-2" />
              <p className="text-gray-500 text-sm font-medium">No referrals yet</p>
              <p className="text-gray-400 text-xs mt-1">Share your code with friends and classmates!</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {referred.map((u, i) => (
                <div key={u.uid} className="flex items-center gap-3 px-4 py-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-base flex-shrink-0 overflow-hidden">
                    {u.photoURL
                      ? <img src={u.photoURL} alt={u.name} className="w-full h-full object-cover" />
                      : u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 line-clamp-1">{u.name || "Student"}</p>
                    <p className="text-xs text-gray-500 line-clamp-1">{u.college || "College not set"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                    </p>
                    {u.verified && <span className="text-xs text-green-600 font-semibold">Verified</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* How it works */}
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
          <p className="font-semibold text-blue-800 text-sm mb-3">How Referrals Work</p>
          <div className="space-y-2">
            {[
              "Share your code with friends",
              "They sign up on CampusSwap using your code",
              "Both of you get verified faster!",
              "Top referrers get a special Ambassador badge",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="w-5 h-5 bg-blue-600 text-white rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <p className="text-xs text-blue-700">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
